/**
 * StatusIndicator Component
 *
 * Displays a colored dot alongside a text label to indicate status.
 * Common pattern for user status, document status, connection state, etc.
 *
 * Unlike {@link Badge}, which wraps content in a colored box,
 * StatusIndicator renders a minimal dot + label inline.
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId, type TestIdProps } from '../utils';

const dotVariants = cva('inline-block shrink-0 rounded-full', {
  variants: {
    color: {
      success: 'bg-success-border',
      warning: 'bg-warn-border',
      error: 'bg-error-border',
      info: 'bg-info-border',
      neutral: 'bg-foreground-muted',
      primary: 'bg-primary-base'
    },
    size: {
      sm: 'w-1.5 h-1.5',
      md: 'w-2 h-2',
      lg: 'w-2.5 h-2.5'
    }
  },
  defaultVariants: {
    color: 'neutral',
    size: 'md'
  }
});

const textVariants = cva('', {
  variants: {
    color: {
      success: 'text-success-text',
      warning: 'text-warn-text',
      error: 'text-error-text',
      info: 'text-info-text',
      neutral: 'text-foreground-muted',
      primary: 'text-primary-base'
    },
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base'
    }
  },
  defaultVariants: {
    color: 'neutral',
    size: 'md'
  }
});

type StatusIndicatorVariantProps = VariantProps<typeof dotVariants>;

export interface StatusIndicatorProps extends StatusIndicatorVariantProps, TestIdProps {
  /** Text label displayed next to the dot */
  label: string;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

/**
 * StatusIndicator — colored dot + label.
 *
 * @example
 * ```tsx
 * <StatusIndicator color="success" label="Active" />
 * <StatusIndicator color="error" label="Suspended" size="sm" />
 * <StatusIndicator color="warning" label="Pending" />
 * ```
 */
export const StatusIndicator = React.forwardRef<HTMLSpanElement, StatusIndicatorProps>(
  ({ color, size, label, className, testId, id, ...props }, ref) => {
    return (
      <span
        ref={ref}
        id={id}
        data-testid={getTestId({ testId, id })}
        className={cn('inline-flex items-center whitespace-nowrap', className)}
        {...props}
      >
        <span className={cn(dotVariants({ color, size }), 'mr-sm')} aria-hidden='true' />
        <span className={textVariants({ color, size })}>{label}</span>
      </span>
    );
  }
);

StatusIndicator.displayName = 'StatusIndicator';
